import React, { forwardRef } from 'react'
import type {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon, KibChevronUpIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'
import { useDebouncedCallback } from 'use-debounce'
import { Defaults, LanguageUtils, NumberUtils } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import {
  getHighValue,
  getIsRetailOrderLineItem,
  getLowValue,
  isRange,
} from '~/components/dashboard/invoices/invoiceUtils'
import ChargesActions from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/ChargesActions'
import DragHandleIcon from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/DragHandleIcon'
import NumberField from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/NumberField'
import { useHasGroupedParentWithFixedPrice } from '~/store/hooks/charges'
import { getInvoiceGroupIdIsExpanded } from '~/store/reducers/finance'
import type { InvoiceLineItem } from '~/types'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChirpEstimateTable.module.scss'

export interface ChirpEstimateTableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  bundleItems?: InvoiceLineItem[]
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  draggableProps?: DraggableProvidedDraggableProps | null
  editDisabled?: boolean
  estimateId: string | undefined
  isBundle?: boolean
  isDragging?: boolean
  isLastChild?: boolean
  isNested?: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  onDeclineClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onDeleteClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onQuantityChange?: (
    item: InvoiceLineItem | RetailOrderLineItem,
    quantityLow: number,
    quantityHigh: number,
  ) => void
  onToggleExpand?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  parentItem?: InvoiceLineItem | RetailOrderLineItem
}

const ChirpEstimateTableRow = forwardRef<
  HTMLTableRowElement,
  ChirpEstimateTableRowProps
>(
  (
    {
      bundleItems,
      dragHandleProps,
      draggableProps,
      editDisabled = false,
      estimateId,
      isBundle = false,
      isDragging = false,
      isLastChild = false,
      isNested = false,
      item,
      onDeclineClick,
      onDeleteClick,
      onQuantityChange,
      onToggleExpand,
      parentItem,
      className,
    },
    ref,
  ) => {
    const { t } = useTranslation('Common')
    const isRetailOrderLineItem = getIsRetailOrderLineItem(item)
    const hasParentWithFixedPrice =
      useHasGroupedParentWithFixedPrice(parentItem)

    // Get expand state for bundles
    const groupId = isRetailOrderLineItem ? null : item.group
    const expandKey = groupId || item.id
    const isExpanded = useSelector(
      getInvoiceGroupIdIsExpanded(expandKey, estimateId),
    )

    // Get item name
    const itemName = isBundle
      ? (item as InvoiceLineItem).groupName ||
        LanguageUtils.getTranslatedFieldName(item) ||
        ''
      : LanguageUtils.getTranslatedFieldName(item) || ''

    // Get quantity values
    const quantityLow = getLowValue(item.quantity)
    const quantityHigh = getHighValue(item.quantity)

    // Get totals from pre-calculated subTotal
    const totalLow = isRetailOrderLineItem
      ? item.totalPrice || 0
      : getLowValue(item.subTotal)
    const totalHigh = isRetailOrderLineItem
      ? item.totalPrice || 0
      : getHighValue(item.subTotal)

    // Check if totals are a range
    const isTotalRange = !isRetailOrderLineItem && isRange(item.subTotal)

    // Debounced handler for quantity changes
    const handleQuantityChange = useDebouncedCallback(
      (newLow: number, newHigh: number) => {
        if (onQuantityChange) {
          onQuantityChange(item, newLow, newHigh)
        }
      },
      Defaults.DEBOUNCE_ACTION_TIME,
    )

    const handleQuantityLowChange = (value: string) => {
      const numericValue = Number.parseFloat(value)
      if (Number.isNaN(numericValue) || numericValue < 0.01) {
        return
      }
      handleQuantityChange(numericValue, quantityHigh)
    }

    const handleQuantityHighChange = (value: string) => {
      const numericValue = Number.parseFloat(value)
      if (Number.isNaN(numericValue) || numericValue < 0.01) {
        return
      }
      handleQuantityChange(quantityLow, numericValue)
    }

    const handleExpandClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onToggleExpand) {
        onToggleExpand(item)
      }
    }

    // NOTE: Must return single <tr> element for react-beautiful-dnd to work correctly.
    // For bundles with children, we render them inside a nested table within a single <td colspan>
    if (isBundle && bundleItems && bundleItems.length > 0) {
      return (
        <tr
          className={classNames(
            styles.dataRow,
            {
              [styles.dataRowDragging]: isDragging,
              [styles.declined]: item.declined,
            },
            className,
          )}
          ref={ref}
          {...draggableProps}
          {...dragHandleProps}
        >
          <td className={styles.bundleCell} colSpan={6}>
            {/* Parent bundle row content - using table for proper column alignment */}
            <table className={styles.bundleParentTable}>
              <tbody>
                <tr className={styles.bundleParentRow}>
                  {/* Item */}
                  <td className={styles.dataCellItem}>
                    <div>
                      <span className={styles.dragHandle}>
                        <DragHandleIcon size={24} />
                      </span>
                      <KibButtonNew
                        aria-label={
                          isExpanded
                            ? t('Common:COLLAPSE_BUNDLE')
                            : t('Common:EXPAND_BUNDLE')
                        }
                        className={styles.expandButton}
                        emphasis="tertiary"
                        icon={
                          isExpanded ? (
                            <KibChevronUpIcon />
                          ) : (
                            <KibChevronDownIcon />
                          )
                        }
                        onClick={handleExpandClick}
                      />
                      <span className={styles.itemName} title={itemName}>
                        {itemName}
                      </span>
                    </div>
                  </td>

                  {/* Empty Quantity Low */}
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <td className={styles.dataCellQuantityLow} />

                  {/* Empty Quantity High */}
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <td className={styles.dataCellQuantityHigh} />

                  {/* Actions */}
                  <td className={styles.dataCellActions}>
                    <div>
                      <ChargesActions
                        showDeclineButton
                        showDeleteButton
                        disabled={editDisabled}
                        showCalculatorButton={false}
                        onDeclineClick={() => onDeclineClick?.(item)}
                        onDeleteClick={() => onDeleteClick?.(item)}
                      />
                    </div>
                  </td>

                  {/* Total Low */}
                  <td className={styles.dataCellTotalLow}>
                    {!item.declined &&
                      isTotalRange &&
                      NumberUtils.formatMoney(totalLow)}
                  </td>

                  {/* Total High */}
                  <td className={styles.dataCellTotalHigh}>
                    {!item.declined &&
                      NumberUtils.formatMoney(
                        isTotalRange ? totalHigh : totalLow,
                      )}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Nested table for bundle children */}
            {isExpanded && (
              <table className={styles.nestedTable}>
                <tbody>
                  {bundleItems.map((childItem, index) => (
                    <ChirpEstimateTableRow
                      isNested
                      editDisabled={editDisabled}
                      estimateId={estimateId}
                      isLastChild={index === bundleItems.length - 1}
                      item={childItem}
                      key={`${childItem.bundleId}_${childItem.id}`}
                      parentItem={item as InvoiceLineItem}
                      onDeclineClick={
                        onDeclineClick
                          ? () => onDeclineClick(childItem)
                          : undefined
                      }
                      onDeleteClick={
                        onDeleteClick
                          ? () => onDeleteClick(childItem)
                          : undefined
                      }
                      onQuantityChange={onQuantityChange}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )
    }

    // Regular row (non-bundle) or nested row
    return (
      <tr
        className={classNames(
          styles.dataRow,
          {
            [styles.dataRowDragging]: isDragging,
            [styles.dataRowNested]: isNested && !isLastChild,
            [styles.declined]: item.declined,
          },
          className,
        )}
        ref={ref}
        {...draggableProps}
        {...dragHandleProps}
      >
        {/* Item */}
        <td className={styles.dataCellItem}>
          <div>
            <span
              className={classNames(styles.dragHandle, {
                [styles.dragHandleNested]: isNested,
              })}
            >
              <DragHandleIcon size={24} />
            </span>
            <span className={styles.itemName} title={itemName}>
              {itemName}
            </span>
          </div>
        </td>

        {/* Quantity Low */}
        <td className={styles.dataCellQuantityLow}>
          {!item.declined && !isBundle && (
            <div className={styles.quantityField}>
              <NumberField
                disabled={editDisabled}
                label=""
                min={0.01}
                step="0.01"
                value={String(quantityLow || 0)}
                onChange={handleQuantityLowChange}
              />
            </div>
          )}
        </td>

        {/* Quantity High */}
        <td className={styles.dataCellQuantityHigh}>
          {!item.declined && !isBundle && (
            <div className={styles.quantityField}>
              <NumberField
                disabled={editDisabled}
                label=""
                min={0.01}
                step="0.01"
                value={String(quantityHigh || 0)}
                onChange={handleQuantityHighChange}
              />
            </div>
          )}
        </td>

        {/* Actions */}
        <td className={styles.dataCellActions}>
          {!isBundle && (
            <div>
              <ChargesActions
                disabled={editDisabled}
                showCalculatorButton={false}
                onDeclineClick={() => onDeclineClick?.(item)}
                onDeleteClick={() => onDeleteClick?.(item)}
              />
            </div>
          )}
        </td>

        {/* Total Low */}
        <td className={styles.dataCellTotalLow}>
          {!item.declined &&
            isTotalRange &&
            !hasParentWithFixedPrice &&
            NumberUtils.formatMoney(totalLow)}
        </td>

        {/* Total High */}
        <td className={styles.dataCellTotalHigh}>
          {!item.declined &&
            !hasParentWithFixedPrice &&
            NumberUtils.formatMoney(isTotalRange ? totalHigh : totalLow)}
        </td>
      </tr>
    )
  },
)

export default ChirpEstimateTableRow
